import Handlebars from "handlebars";

// Expose equality helper so templates can branch without bundler support
Handlebars.registerHelper('eq', (left, right) => left === right);

const ICON_LOOKUP = {
	product: 'product',
	products: 'product',
	collection: 'collection',
	collections: 'collection',
	content: 'content',
	resource: 'resource',
	resources: 'resource'
};

const TEMPLATE_FALLBACK = () => '';

function resolveIconKey(raw) {
	if (!raw) return '';
	const key = String(raw).trim().toLowerCase();
	return ICON_LOOKUP[key] || '';
}

function createSearchPageView(container) {
	const refs = {
		headerInputs: Array.from(document.querySelectorAll('.header-nav__search-bar')),
		results: container.querySelector('#search-results'),
		summary: container.querySelector('#search-summary'),
		progressWrap: container.querySelector('#search-progress'),
		progressText: container.querySelector('#searchProgressText'),
		progressBar: container.querySelector('#searchProgressBar'),
		loadMore: container.querySelector('#load-more-results'),
		loading: container.querySelector('#search-loading'),
		error: container.querySelector('#search-error'),
		filters: container.querySelector('.search-page__filters'),
		sortSelect: container.querySelector('#search-sort')
	};

	function setLoading(isLoading) {
		if (refs.loading) {
			refs.loading.hidden = !isLoading;
		}

		if (refs.loadMore && !refs.loadMore.hidden) {
			refs.loadMore.disabled = Boolean(isLoading);
		}
	}

	function setError(message) {
		if (!refs.error) return;
		if (message) {
			refs.error.textContent = message;
			refs.error.hidden = false;
		} else {
			refs.error.textContent = '';
			refs.error.hidden = true;
		}
	}

	function renderResults(items, template, append = false) {
		if (!refs.results) return;
		const list = Array.isArray(items) ? items : [];
		const templateFn = typeof template === 'function' ? template : TEMPLATE_FALLBACK;

		if (!append) {
			refs.results.innerHTML = '';
		}

		if (list.length === 0) {
			return;
		}

		const html = list.map(item => {
			const title = item?.title || 'Untitled';
			const url = item?.url || '#';
			const excerpt = item?.excerpt || '';
			const typeLabel = typeof item?.contentType === 'string' ? item.contentType : '';
			const normalizedType = typeLabel || '';
			const icon = resolveIconKey(typeLabel) || resolveIconKey(item?.icon);
			const lowerType = normalizedType.trim().toLowerCase();
			const target = lowerType === 'resource' || lowerType === 'resources' ? '_blank' : '_self';
			const hasPrimaryCategory = Boolean(item?.primaryCategoryName);
			const hasSecondaryCategory = Boolean(item?.secondaryCategoryName);
			const categoryCount = hasPrimaryCategory && hasSecondaryCategory ? 2 : hasPrimaryCategory ? 1 : 0;

			return templateFn({
				title,
				imageUrl: item?.imageUrl,
				url,
				excerpt,
				type: normalizedType,
				icon,
				target,
				categoryCount,
				primaryCategoryName: item?.primaryCategoryName,
				secondaryCategoryName: item?.secondaryCategoryName
			});
		}).join('');

		if (append) {
			refs.results.insertAdjacentHTML('beforeend', html);
		} else {
			refs.results.innerHTML = html;
		}
	}

	function renderSummary(total, term) {
		if (!refs.summary) return;
		refs.summary.textContent = '';
		if (!term) return;

		const summaryText = `${total.toLocaleString()} result${total === 1 ? '' : 's'} for `;
		const emphasisedTerm = document.createElement('strong');
		emphasisedTerm.textContent = `"${term}"`;

		refs.summary.append(summaryText, emphasisedTerm);
	}

	function updateProgress(displayed, total, moreAvailable = false) {
		if (!refs.progressWrap) return;

		const shownCount = Math.max(0, Number(displayed) || 0);
		const totalRaw = Number(total);
		const totalCount = Number.isFinite(totalRaw) && totalRaw > 0 ? totalRaw : shownCount;

		if (!totalCount) {
			refs.progressWrap.hidden = true;
			if (refs.progressText) refs.progressText.textContent = 'Showing 0 of 0';
			if (refs.progressBar) refs.progressBar.style.width = '0%';
			if (refs.loadMore) {
				refs.loadMore.hidden = true;
				refs.loadMore.disabled = false;
			}
			return;
		}

		refs.progressWrap.hidden = false;
		if (refs.progressText) {
			refs.progressText.textContent = `Showing ${shownCount} of ${totalCount}`;
		}

		if (refs.progressBar) {
			const percent = totalCount > 0 ? Math.min(100, (shownCount / totalCount) * 100) : 0;
			refs.progressBar.style.width = `${percent}%`;
		}

		if (refs.loadMore) {
			const showLoadMore = Boolean(moreAvailable);
			refs.loadMore.hidden = !showLoadMore;
			refs.loadMore.disabled = !showLoadMore;
		}
	}

	function syncActiveFilter(activeType) {
		if (!refs.filters) return;
		const targetType = (activeType || 'All').toString().toLowerCase();

		refs.filters.querySelectorAll('.search-page__filter-btn').forEach(btn => {
			const type = (btn.getAttribute('data-content-type') || 'All').toLowerCase();
			const isActive = type === targetType;
			btn.classList.toggle('is-active', isActive);
			btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
		});
	}

	function updateSort(value) {
		if (!refs.sortSelect) return;
		refs.sortSelect.value = value || 'Default';
	}

	function updateHeaderInputs(term) {
		refs.headerInputs.forEach(el => {
			if (el instanceof HTMLInputElement) {
				el.value = term || '';
			}
		});
	}

	return {
		refs,
		setLoading,
		setError,
		renderResults,
		renderSummary,
		updateProgress,
		syncActiveFilter,
		updateSort,
		updateHeaderInputs
	};
}

function updateUrlParameters(term, page, type, sort) {
	try {
		const url = new URL(window.location.href);

		if (term) {
			url.searchParams.set('query', term);
		} else {
			url.searchParams.delete('query');
		}

		if (page && page > 1) {
			url.searchParams.set('page', String(page));
		} else {
			url.searchParams.delete('page');
		}

		if (type && type !== 'All') {
			url.searchParams.set('type', type);
		} else {
			url.searchParams.delete('type');
		}

		if (sort && sort !== 'Default') {
			url.searchParams.set('sort', sort);
		} else {
			url.searchParams.delete('sort');
		}

		window.history.replaceState({}, '', url);
	} catch (error) {
		console.error('error updating url', error);
	}
}

export function initSearchPage() {
	const container = document.querySelector('.search-page');
	if (!container) return;

	const view = createSearchPageView(container);
	const { refs } = view;
	const { loadMore: loadMoreBtn, filters: filtersWrap, sortSelect } = refs;

	const pageSizeAttr = parseInt(container.getAttribute('data-page-size') || '16', 10);
	const pageSize = Number.isFinite(pageSizeAttr) && pageSizeAttr > 0 ? pageSizeAttr : 16;

	const state = {
		term: '',
		page: 1,
		type: 'All',
		sort: 'Default',
		total: 0,
		displayed: 0
	};

	view.syncActiveFilter(state.type);

	const templateScript = document.getElementById('search-page-result-template');
	const itemTemplate = templateScript ? Handlebars.compile(templateScript.innerHTML) : TEMPLATE_FALLBACK;


	async function performSearch(term, page = 1, options = {}) {
		const { append = false } = options;
		const normalizedTerm = append ? state.term : String(term || '').trim();

		if (!append) {
			state.term = normalizedTerm;
			state.page = 1;
			state.total = 0;
			state.displayed = 0;
			view.renderResults([], itemTemplate, false);
			view.renderSummary(0, '');
			view.updateProgress(0, 0, false);
		}

		view.setError('');

		if (!normalizedTerm) {
			updateUrlParameters('', 1, state.type, state.sort);
			return null;
		}

		const targetPage = page < 1 ? 1 : page;

		view.setLoading(true);
		try {
			const params = new URLSearchParams({
				term: normalizedTerm,
				pageSize: String(pageSize),
				page: String(targetPage),
				contentType: state.type,
				sort: state.sort
			});

			const response = await fetch(`/api/search?${params.toString()}`, { headers: { Accept: 'application/json' } });
			if (!response.ok) throw new Error(`Search failed (${response.status})`);

			const payload = await response.json();
			const itemsSource = payload.items || payload.Items || [];
			const items = Array.isArray(itemsSource) ? itemsSource : [];

			const totalFromResponse = payload.totalItems ?? payload.TotalItems ?? payload.total ?? payload.Total ?? payload.count ?? payload.Count ?? 0;
			const numericTotal = Number(totalFromResponse);
			const hasExplicitTotal = Number.isFinite(numericTotal) && numericTotal > 0;

			view.renderResults(items, itemTemplate, append);

			const addedCount = items.length;
			state.displayed = append ? state.displayed + addedCount : addedCount;

			if (hasExplicitTotal) {
				state.total = numericTotal;
			} else if (!append) {
				state.total = state.displayed;
			} else {
				state.total = Math.max(state.total, state.displayed);
			}

			if (state.total > 0) {
				state.displayed = Math.min(state.displayed, state.total);
			}

			state.page = targetPage;

			const hasMoreFlag = payload.hasMore ?? payload.HasMore ?? null;
			let moreAvailable;
			if (typeof hasMoreFlag === 'boolean') {
				moreAvailable = hasMoreFlag;
			} else if (state.total > 0) {
				moreAvailable = state.displayed < state.total;
			} else {
				moreAvailable = addedCount === pageSize;
			}

			const totalForSummary = state.total > 0 ? state.total : state.displayed;
			view.renderSummary(totalForSummary, normalizedTerm);
			view.updateProgress(state.displayed, totalForSummary, moreAvailable);
			view.syncActiveFilter(state.type);

			state.term = normalizedTerm;
			updateUrlParameters(state.term, state.page, state.type, state.sort);

			if (!moreAvailable && loadMoreBtn) {
				loadMoreBtn.hidden = true;
				loadMoreBtn.disabled = true;
			}

			return {
				items,
				totalItems: totalForSummary,
				displayedItems: state.displayed,
				page: state.page,
				hasMore: Boolean(moreAvailable)
			};
		} catch (error) {
			const message = error && error.message ? error.message : 'An error occurred while searching.';
			view.setError(message);
			return null;
		} finally {
			view.setLoading(false);
		}
	}

	if (loadMoreBtn) {
		loadMoreBtn.addEventListener('click', () => {
			if (loadMoreBtn.disabled) return;
			if (!state.term) return;
			performSearch(state.term, state.page + 1, { append: true });
		});
	}

	// Initialize from URL
	try {
		const url = new URL(window.location.href);
		const initialTerm = url.searchParams.get('query') || '';
		const initialPage = parseInt(url.searchParams.get('page') || '1', 10) || 1;
		const initialType = url.searchParams.get('type') || 'All';
		const initialSort = url.searchParams.get('sort') || 'Default';

		state.type = initialType;
		state.sort = initialSort;
		view.updateSort(state.sort);
		view.updateHeaderInputs(initialTerm);
		view.syncActiveFilter(state.type);

		if (initialTerm) {
			const targetPage = initialPage > 1 ? initialPage : 1;
			performSearch(initialTerm, 1).then(async (result) => {
				if (!result) return;
				if (targetPage <= 1) return;

				for (let pageNumber = 2; pageNumber <= targetPage; pageNumber += 1) {
					const nextResult = await performSearch(initialTerm, pageNumber, { append: true });
					if (!nextResult || nextResult.items.length === 0 || !nextResult.hasMore) {
						break;
					}
				}
			});
		} else {
			updateUrlParameters('', 1, state.type, state.sort);
		}
	} catch (error) {
		console.error('error initializing search page', error);
	}

	// Wire up filter buttons
	if (filtersWrap) {
		filtersWrap.addEventListener('click', (ev) => {
			const trigger = ev.target instanceof HTMLElement ? ev.target : null;
			if (!trigger) return;

			const btn = trigger.closest('.search-page__filter-btn');
			if (!btn) return;

			const nextType = btn.getAttribute('data-content-type') || 'All';
			if (state.type === nextType) return;

			state.type = nextType;
			view.syncActiveFilter(state.type);

			if (state.term) {
				performSearch(state.term, 1);
			} else {
				updateUrlParameters('', 1, state.type, state.sort);
			}
		});
	}

	// Wire up sort dropdown
	if (sortSelect) {
		sortSelect.addEventListener('change', () => {
			const nextSort = String(sortSelect.value || 'Default');
			if (state.sort === nextSort) return;

			state.sort = nextSort;
			if (state.term) {
				performSearch(state.term, 1);
			} else {
				updateUrlParameters('', 1, state.type, state.sort);
			}
		});
	}
}